export type PlanTier = "starter" | "pro" | "careerMax" | "ultra" | null;

export type Feature =
  | "ai-basic"
  | "ai-full"
  | "ai-chat"
  | "auto-tailor"
  | "ats-score"
  | "job-match"
  | "cover-letter"
  | "pdf-export"
  | "docx-export"
  | "linkedin-import"
  | "basic-templates"
  | "premium-templates"
  | "all-fonts"
  | "unlimited-resumes"
  | "priority-ai"
  | "bulk-ops"
  | "json-export"
  | "early-access";

export const TIER_RANK: Record<NonNullable<PlanTier>, number> = {
  starter: 1,
  pro: 2,
  careerMax: 3,
  ultra: 4,
};

export const FEATURE_MIN_TIER: Record<Feature, NonNullable<PlanTier>> = {
  "ai-basic": "starter",
  "pdf-export": "starter",
  "basic-templates": "starter",
  "ai-full": "pro",
  "ats-score": "pro",
  "job-match": "pro",
  "cover-letter": "pro",
  "docx-export": "pro",
  "linkedin-import": "pro",
  "premium-templates": "pro",
  "all-fonts": "pro",
  "auto-tailor": "careerMax",
  "unlimited-resumes": "careerMax",
  "ai-chat": "careerMax",
  "priority-ai": "careerMax",
  "bulk-ops": "ultra",
  "json-export": "ultra",
  "early-access": "ultra",
};

export const PLAN_DISPLAY_NAMES: Record<NonNullable<PlanTier>, string> = {
  starter: "Starter",
  pro: "Pro",
  careerMax: "Career Max",
  ultra: "Ultra",
};

/**
 * Maps each tier to the uppercase key segment used in env var names.
 * e.g. careerMax → CAREERMAX, so the env var is STRIPE_PRICE_CAREERMAX_MONTHLY.
 */
export const TIER_ENV_KEYS: Record<NonNullable<PlanTier>, string> = {
  starter: "STARTER",
  pro: "PRO",
  careerMax: "CAREERMAX",
  ultra: "ULTRA",
};

export type BillingInterval = "monthly" | "annual";

export const PLAN_TIERS: NonNullable<PlanTier>[] = ["starter", "pro", "careerMax", "ultra"];

export const BILLING_INTERVALS: BillingInterval[] = ["monthly", "annual"];

/**
 * Catalog price (in USD, no decimals) charged at checkout for each
 * tier + interval combination. Keep this aligned with the Stripe price list
 * and the marketing pricing card. Used by analytics to populate `value`
 * and `items[].price` on `purchase` / `register_subscribe` events so we can
 * report ARR/LTV in GA4 without server round-trips.
 *
 * Annual rows are the full annual charge, NOT the per-month equivalent.
 */
export const PLAN_PRICES_USD: Record<NonNullable<PlanTier>, Record<BillingInterval, number>> = {
  starter: { monthly: 9, annual: 69 },
  pro: { monthly: 19, annual: 139 },
  careerMax: { monthly: 39, annual: 259 },
  ultra: { monthly: 79, annual: 529 },
};

export function getPlanPriceUsd(tier: NonNullable<PlanTier>, interval: BillingInterval): number {
  return PLAN_PRICES_USD[tier][interval];
}

/**
 * Build the env var suffix for a given tier and interval.
 * e.g. ("careerMax", "monthly") → "PRICE_CAREERMAX_MONTHLY"
 */
export function buildPriceSuffix(tier: NonNullable<PlanTier>, interval: BillingInterval): string {
  return `PRICE_${TIER_ENV_KEYS[tier]}_${interval.toUpperCase()}`;
}

/** Stripe Checkout trial length for first-time subscribers. */
export const BILLING_TRIAL_DAYS = 3;

/**
 * Instant when the paid product came back. Accounts with a resume created
 * before this keep download/export rights for those resumes. Override with
 * `BILLING_CUTOFF_AT` / `VITE_BILLING_CUTOFF_AT` at deploy so late free-era
 * signups are still grandfathered.
 */
export const DEFAULT_BILLING_CUTOFF_AT = "2026-08-24T17:58:11.000Z";

export function getBillingCutoffAt(override?: string | null): Date {
  const raw = override?.trim() || DEFAULT_BILLING_CUTOFF_AT;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return new Date(DEFAULT_BILLING_CUTOFF_AT);
  return parsed;
}

export function isCreatedBeforeCutoff(
  createdAt: Date | string,
  cutoff: Date = getBillingCutoffAt(),
): boolean {
  return new Date(createdAt).getTime() < cutoff.getTime();
}

export function canAccessFeature(userTier: PlanTier, feature: Feature): boolean {
  if (!userTier) return false;
  const minTier = FEATURE_MIN_TIER[feature];
  return TIER_RANK[userTier] >= TIER_RANK[minTier];
}

/** Paid feature access, or unpaid legacy export of a pre-cutoff resume. */
export function canExportResume(
  userTier: PlanTier,
  feature: Feature,
  createdAt: Date | string,
  cutoff?: Date,
): boolean {
  if (canAccessFeature(userTier, feature)) return true;
  if (userTier) return false;
  if (feature !== "pdf-export" && feature !== "docx-export" && feature !== "json-export") {
    return false;
  }
  return isCreatedBeforeCutoff(createdAt, cutoff ?? getBillingCutoffAt());
}

export function getMinTierForFeature(feature: Feature): NonNullable<PlanTier> {
  return FEATURE_MIN_TIER[feature];
}

export function getPlanDisplayName(tier: PlanTier): string | null {
  return tier ? PLAN_DISPLAY_NAMES[tier] : null;
}

export function isUpgrade(currentTier: PlanTier, newTier: NonNullable<PlanTier>): boolean {
  if (!currentTier) return true;
  return TIER_RANK[newTier] > TIER_RANK[currentTier];
}

export function isPlanTier(value: string | null | undefined): value is NonNullable<PlanTier> {
  return !!value && (PLAN_TIERS as string[]).includes(value);
}

export function isBillingInterval(value: string | null | undefined): value is BillingInterval {
  return !!value && (BILLING_INTERVALS as string[]).includes(value);
}
